import { useTheme } from '@mui/material';
import {
  DEFAULT_DATE_FORMAT_FNS,
  GroupValidity,
  JustificationCounts,
  useGetAttendanceStatisticsQuery,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { format } from 'date-fns';
import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useAttendanceCodes } from '../../../hooks/useAttendanceCodes';
import { getPresentAbsentCodeIds, hasJustificationCounts } from '../attendance-utils';
import {
  AttendanceChartCard,
  AttendanceStatsJustifications,
  AttendanceStatsSkeleton,
} from '../AttendanceStatistics/AttendanceStats';
import { AttendanceStatsLayout } from '../AttendanceStatistics/AttendanceStats.styled';
import { PERCENT_PRECISION } from './common/constants';

const PERIOD_STAT_NAME = 'period';

export const roundTo = (value: number, precision: number): number => {
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
};

export const calculatePresentTrackedTotals = (
  counts: readonly { attendance_code_id: string | null; count: number }[],
  presentCodeIds: Set<string>,
): { totalPresent: number; totalTracked: number } => {
  let totalPresent = 0;
  let totalTracked = 0;
  for (const detail of counts) {
    if (!detail.attendance_code_id) continue;
    totalTracked += detail.count;
    if (presentCodeIds.has(detail.attendance_code_id)) totalPresent += detail.count;
  }
  return { totalPresent, totalTracked };
};

type AttendanceStatsSummaryCardsProps = {
  loading?: boolean;
  showStats: boolean;
  presentPercent: number;
  absentPercent: number;
  justificationCounts?: JustificationCounts;
};

export const AttendanceStatsSummaryCards: FC<AttendanceStatsSummaryCardsProps> = ({
  loading,
  showStats,
  presentPercent,
  absentPercent,
  justificationCounts,
}) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const { attendanceCodes, isLoading: codesLoading } = useAttendanceCodes();

  const showJustificationCard = useMemo(() => {
    const hasJustifications = !!justificationCounts && hasJustificationCounts(justificationCounts);
    return hasJustifications || attendanceCodes.some((code) => code.has_justification);
  }, [attendanceCodes, justificationCounts]);

  const isLoading = loading || codesLoading;

  if (!isLoading && !showStats && !showJustificationCard) return null;

  return (
    <AttendanceStatsLayout>
      {isLoading ? (
        <AttendanceStatsSkeleton />
      ) : (
        <>
          {showStats && (
            <>
              <AttendanceChartCard
                title={formatMessage({ id: 'attendance-Present' })}
                statistics={[{ value: presentPercent, name: PERIOD_STAT_NAME }]}
                primaryColor={theme.palette.success.main}
                showChartBars={false}
              />
              <AttendanceChartCard
                title={formatMessage({ id: 'attendance-Absent' })}
                statistics={[{ value: absentPercent, name: PERIOD_STAT_NAME }]}
                primaryColor={theme.palette.error.main}
                showChartBars={false}
              />
            </>
          )}
          {showJustificationCard && (
            <AttendanceStatsJustifications justificationCounts={justificationCounts} />
          )}
        </>
      )}
    </AttendanceStatsLayout>
  );
};

type AttendanceGroupStatsSummaryProps = {
  groupId: string;
  validity: GroupValidity | undefined;
};

export const AttendanceGroupStatsSummary: FC<AttendanceGroupStatsSummaryProps> = ({
  groupId,
  validity,
}) => {
  const { schoolId = '' } = useAuth();
  const { attendanceCodes } = useAttendanceCodes();

  const today = format(new Date(), DEFAULT_DATE_FORMAT_FNS);
  const dateFrom = validity?.date_from ?? validity?.from_school_year?.start ?? '';
  const dateTo = validity?.date_to ?? validity?.to_school_year?.end ?? today;

  const { data, isLoading } = useGetAttendanceStatisticsQuery(
    { schoolId, groupId, dateFrom, dateTo },
    { enabled: !!schoolId && !!groupId && !!dateFrom, refetchOnMount: 'always' },
  );

  const periodCounts = data?.statistic.period ?? [];

  const stats = useMemo(() => {
    const presentCodeIds = new Set(getPresentAbsentCodeIds(attendanceCodes).present);
    const { totalPresent, totalTracked } = calculatePresentTrackedTotals(
      periodCounts,
      presentCodeIds,
    );

    if (!totalTracked) {
      return { presentPercent: 0, absentPercent: 0 };
    }

    const presentPercent = roundTo((totalPresent / totalTracked) * 100, PERCENT_PRECISION);
    return {
      presentPercent,
      absentPercent: roundTo(100 - presentPercent, PERCENT_PRECISION),
    };
  }, [periodCounts, attendanceCodes]);

  if (!dateFrom) return null;

  return (
    <AttendanceStatsSummaryCards
      loading={isLoading}
      showStats={periodCounts.length > 0}
      presentPercent={stats.presentPercent}
      absentPercent={stats.absentPercent}
      justificationCounts={data?.justification_counts}
    />
  );
};
